/**
 * ActionButton renders a dialog action button with focus styling.
 *
 * Why: Dialog action buttons (Cancel, Confirm, etc.) share common patterns:
 * focus ring styling, tabIndex management, optional accent background.
 * This component captures that pattern to reduce repetition.
 *
 * When to use: Use in dialogs for action buttons like Cancel, Confirm, Close.
 * Pass `isFocused` based on the navigation state to get proper focus styling.
 */
import * as React from 'react'
import { Button } from '@merels/ui'
import { cn } from '@merels/ui'
import { useTheme } from '../../../contexts/ThemeContext'

export interface ActionButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  /** Whether this button is currently focused (for focus ring styling) */
  isFocused?: boolean
  /** Button variant */
  variant?: 'outline' | 'default'
  /** Whether to use accent color for background (for primary actions) */
  accentBackground?: boolean
  /** Whether to add glow effect when focused (for primary actions) */
  glowOnFocus?: boolean
  /** Test ID for E2E tests */
  testId?: string
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ isFocused, variant = 'outline', accentBackground, glowOnFocus, testId, className, children, ...props }, ref) => {
    const { getAppColor } = useTheme()
    const appColorShades = getAppColor()
    const accentColor = appColorShades.main
    const backgroundColor = appColorShades.background

    const focusRing = isFocused && accentColor
      ? `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`
      : undefined

    const glow = glowOnFocus && isFocused && accentColor
      ? `, 0 0 12px ${accentColor}60`
      : ''

    return (
      <Button
        ref={ref}
        variant={variant}
        data-testid={testId}
        className={cn(
          isFocused && "outline-none",
          className
        )}
        style={{
          ...(accentBackground && accentColor ? {
            backgroundColor: accentColor,
            color: 'white',
            borderColor: accentColor,
          } : {}),
          ...(focusRing ? {
            boxShadow: focusRing + glow,
          } : {}),
        }}
        tabIndex={isFocused ? 0 : -1}
        {...props}
      >
        {children}
      </Button>
    )
  }
)
ActionButton.displayName = 'ActionButton'
